import type { ViewState } from "../../types/warehouse.types";
import type { BufferedCacheConfig, Size, TransformFunction } from "./types";

interface CacheState {
  scale: number;
  rotation: number;
  width: number;
  height: number;
  dataVersion: number;
  cachedOffsetX: number;
  cachedOffsetY: number;
}

/**
 * Offscreen canvas cache with buffering for smooth panning.
 * Renders to a larger canvas than visible area, allowing pan without redraw.
 * Invalidates on zoom/rotation/data changes.
 *
 * @example
 * const cache = new BufferedCanvasCache({ bufferSize: 500, panThreshold: 200 });
 * cache.render(ctx, viewState, canvasSize, worldToScreen, dataVersion, renderCallback);
 * cache.dispose();
 */
export class BufferedCanvasCache {
  private offscreenCanvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private lastState: CacheState;
  private readonly BUFFER_SIZE: number;
  private readonly PAN_THRESHOLD: number;
  private cachedCos = 1;
  private cachedSin = 0;
  private bufferedWorldToScreen: TransformFunction | null = null;
  private currentPanShift = { deltaX: 0, deltaY: 0 };

  constructor(config: BufferedCacheConfig = {}) {
    const { bufferSize = 500, panThreshold = 200 } = config;
    this.BUFFER_SIZE = bufferSize;
    this.PAN_THRESHOLD = panThreshold;
    this.offscreenCanvas = document.createElement('canvas');
    const ctx = this.offscreenCanvas.getContext('2d');
    if (!ctx) throw new Error("Failed to create offscreen context");
    this.ctx = ctx;

    this.lastState = {
      scale: 0,
      rotation: 0,
      width: 0,
      height: 0,
      dataVersion: 0,
      cachedOffsetX: 0,
      cachedOffsetY: 0
    };
  }

  public render(
    destCtx: CanvasRenderingContext2D,
    viewState: ViewState,
    canvasSize: Size,
    worldToScreen: TransformFunction,
    dataVersion: number,
    renderFn: (
      ctx: CanvasRenderingContext2D,
      bufferedWorldToScreen: TransformFunction,
      bufferedSize: Size
    ) => void
  ): void {
    const needsFullRedraw = this.needsFullRedraw(viewState, canvasSize, dataVersion);

    if (!needsFullRedraw) {
      const { deltaX, deltaY } = this.calculatePanShift(viewState);

      if (Math.abs(deltaX) > this.PAN_THRESHOLD || Math.abs(deltaY) > this.PAN_THRESHOLD) {
        this.redrawCache(viewState, canvasSize, worldToScreen, dataVersion, renderFn);
        this.currentPanShift.deltaX = 0;
        this.currentPanShift.deltaY = 0;
        destCtx.drawImage(this.offscreenCanvas, -this.BUFFER_SIZE, -this.BUFFER_SIZE);
        return;
      }

      this.currentPanShift.deltaX = deltaX;
      this.currentPanShift.deltaY = deltaY;
      destCtx.drawImage(this.offscreenCanvas, -this.BUFFER_SIZE + deltaX, -this.BUFFER_SIZE + deltaY);
    } else {
      this.redrawCache(viewState, canvasSize, worldToScreen, dataVersion, renderFn);
      this.currentPanShift.deltaX = 0;
      this.currentPanShift.deltaY = 0;
      destCtx.drawImage(this.offscreenCanvas, -this.BUFFER_SIZE, -this.BUFFER_SIZE);
    }
  }

  private calculatePanShift(viewState: ViewState): { deltaX: number; deltaY: number } {
    const dWx = viewState.offsetX - this.lastState.cachedOffsetX;
    const dWy = viewState.offsetY - this.lastState.cachedOffsetY;
    const deltaX = (dWx * this.cachedCos - dWy * this.cachedSin) * viewState.scale;
    const deltaY = (dWx * this.cachedSin + dWy * this.cachedCos) * viewState.scale;
    return { deltaX, deltaY };
  }

  private needsFullRedraw(viewState: ViewState, canvasSize: Size, dataVersion: number): boolean {
    return (
      this.lastState.scale !== viewState.scale ||
      this.lastState.rotation !== viewState.rotation ||
      this.lastState.width !== canvasSize.width ||
      this.lastState.height !== canvasSize.height ||
      this.lastState.dataVersion !== dataVersion
    );
  }

  private redrawCache(
    viewState: ViewState,
    canvasSize: Size,
    originalWorldToScreen: TransformFunction,
    dataVersion: number,
    renderFn: (ctx: CanvasRenderingContext2D, bufferedWorldToScreen: TransformFunction, bufferedSize: Size) => void
  ): void {
    const bufferWidth = canvasSize.width + this.BUFFER_SIZE * 2;
    const bufferHeight = canvasSize.height + this.BUFFER_SIZE * 2;

    if (this.offscreenCanvas.width !== bufferWidth || this.offscreenCanvas.height !== bufferHeight) {
      this.offscreenCanvas.width = bufferWidth;
      this.offscreenCanvas.height = bufferHeight;
    } else {
      this.ctx.clearRect(0, 0, bufferWidth, bufferHeight);
    }

    this.cachedCos = Math.cos(viewState.rotation);
    this.cachedSin = Math.sin(viewState.rotation);

    const BUFFER = this.BUFFER_SIZE;
    this.bufferedWorldToScreen = (x: number, y: number) => {
      const p = originalWorldToScreen(x, y);
      return { x: p.x + BUFFER, y: p.y + BUFFER };
    };

    renderFn(this.ctx, this.bufferedWorldToScreen, {
      width: bufferWidth,
      height: bufferHeight,
      bufferOffset: BUFFER
    });

    this.lastState = {
      scale: viewState.scale,
      rotation: viewState.rotation,
      width: canvasSize.width,
      height: canvasSize.height,
      dataVersion,
      cachedOffsetX: viewState.offsetX,
      cachedOffsetY: viewState.offsetY
    };
  }

  public dispose(): void {
    this.offscreenCanvas.width = 1;
    this.offscreenCanvas.height = 1;
  }

  /**
   * Get current pan shift for correcting hit detection during buffered panning
   */
  public getPanShift(): { deltaX: number; deltaY: number } {
    return this.currentPanShift;
  }
}
